<template>
    <div class="mt--10">
        <div class="client__notes">
            <template v-if="fetchNotes.loading">
                <PageSpinner />
            </template>

            <template v-else-if="fetchNotes.err">
                <EmptyPage state="error" @action="handleFetchClientNotes" />
            </template>

            <template v-else>
                <EmptyPage
                    v-if="!notes.length" 
                    type="note" 
                    icon-name="notes" 
                    state="empty" 
                />

                <div v-else>
                    <div v-for="item in notes" :key="item._id" class="client__notes__item" :class="{ 'disabled': currentNoteId === item._id && deleteLoading }">
                        <div class="text--sm">
                            <p>{{ sanitizeNoteContent(item.value) }}</p>
                            
                            <p class="text--xs text-gray-400 mt--5 text--semi--bold">
                                {{ formatDateStrings(item.createdAt) }}
                            </p>
                        </div>

                        <div class="dropdown">
                            <button 
                                id="noteDropdown" 
                                class="btn--ghost btn text--xs btn-sm text--bold" 
                                type="button"
                                aria-expanded="false"
                                data-bs-toggle="dropdown" 
                                data-bs-auto-close="true" 
                            >
                                <span class="mr--5 flex align-items-center">
                                    <icon-svg name="dots-horizontal-rounded" fill="#637579" width="12px" height="12px" />
                                </span>
                            </button>

                            <List id="note">
                                <ListItem flex pointer class="text--xs" @click="handleCopyNoteContent(item)">
                                    <IconSvg class="mr--5" name="copy" fill="#6a7381" /> 
                                    <span class="text--sm">Copy content</span>
                                </ListItem>
                                <ListItem flex pointer class="text--xs" @click="startNoteEdit(item)">
                                    <IconSvg class="mr--5" name="edit" fill="#6a7381" /> 
                                    <span class="text--sm">Edit note</span>
                                </ListItem>
                                <ListItem flex pointer class="text--xs" @click="handleDeleteNote(item)">
                                    <IconSvg class="mr--5" name="delete" fill="#e04060" /> 
                                    <span class="text--sm text-error-400">Delete note</span>
                                </ListItem>
                            </List>
                        </div>
                    </div>
                </div>

                <div class="w-100">
                    <form class="client__notes__form" @submit.prevent>
                        <div class="form__textarea__group">
                            <div class="form__textarea__wrap">
                                <TipTap v-model="noteValue" />
                            </div>

                            <div class="client__notes__action__btns width-100">
                                <div>
                                    <p class="table__striped__footer__results text--xs pl--15">
                                        COUNT: <span class="text--medium" :class="{ 'text-error-400': noteValue.length > 500 }">{{ noteValue.length > 500 ? '-' : '' }}{{ noteValue.length }}</span>
                                    </p>
                                </div>
                                <div v-if="showActionBtns" class="flex align-items-center pr--10">
                                    <BaseButton
                                        class="mr--5" 
                                        label="Cancel" 
                                        variant="outline" 
                                        @submit="resetForm"
                                    />
                                    <BaseButton 
                                        type="submit" 
                                        :loading="loading"
                                        :disabled="noteValue.length > 500 || !noteValue"
                                        :label="action === 'add' ? 'Add' : 'Save'" 
                                        @submit="handleFormSubmit"
                                    />
                                </div>
                            </div>
                        </div>
                    </form>
                </div>
            </template>
        </div>
    </div>
</template>

<script>
import { IconSvg, EmptyPage, PageSpinner, BaseButton, List, ListItem } from "@/components/ui/index";
import { replaceStringsAll } from "@/plugins/formatters";
import { formatDateStrings } from "@/utils/date";
import TipTap from "@/components/ui/editor/TipTap.vue";
import sanitizeHtml from "sanitize-html";

export default {
    name: "ClientNotes",

    components: {
        IconSvg,
        EmptyPage,
        PageSpinner,
        BaseButton,
        TipTap,
        List, 
        ListItem,
    },

    data() {
        return {
            noteValue: "",
            currentNoteId: "",
            action: "add",
            showActionBtns: true,
            fetchNotes: {
                loading: false,
                err: false,
                msg: "",
            },
            loading: false,
            deleteLoading: false,
        };
    },

    computed: {
        pageData() {
            return this.$store.getters["clients/currentClient"]?.notes?.pageData;
        },

        notes() {            
            return this.$store.getters["clients/currentClient"]?.notes?.list || [];
        },
    },

    async mounted() {
        if (!this.notes.length) {
            await this.handleFetchClientNotes();
        }
    },

    methods: {
        replaceStringsAll,
        formatDateStrings,

        sanitizeNoteContent(text) {
            return sanitizeHtml(text, {
                allowedTags: ["a"],
                allowedAttributes: {
                    "a": [ "href" ],
                },
            });
        },

        setCurrentNoteValue() {
            this.noteValue = note.value;
        },

        startNoteEdit(note) {
            if (!note?._id || !note?.value) return;

            this.currentNoteId = note?._id;
            this.noteValue = note.value;

            this.action = "edit";
            // this.showActionBtns = true;
        },

        resetForm() {
            this.noteValue = "";
            this.action = "";
            // this.showActionBtns = false;
            this.currentNoteId = "";
        },

        async handleFetchClientNotes(params) {
            try {
                this.fetchNotes.err = false;
                this.fetchNotes.loading = true;

                const payload = {
                    page: params?.page || this.pageData?.currentPage,
                };

                await this.$store.dispatch("clients/getClientNotes", { id: this.$route.params.id, params: payload });
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;

                this.fetchNotes.err = true;
                this.fetchNotes.msg = errMsg;
            } finally {
                this.fetchNotes.loading = false;
            }
        },
        
        async handleAddNote() {
            try {
                const id = this.$route.params.id;

                if (!id || !this.noteValue) {
                    this.$toast.error("Missing route id or note value");                
                }

                this.loading = true;

                const regex =  /<\/?p>/g;

                const payload = {
                    refId: this.$route.params.id,
                    value: replaceStringsAll(this.noteValue, regex),
                    clientId: id,
                };

                const res = await this.$store.dispatch("clients/addNewNote", payload);

                this.notes = [ ...this.notes, res ];
                this.pageData.total = this.pageData.total + 1;
                this.pageData.totalPages = Math.ceil(this.pageData.total / 50);
                this.resetForm();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loading = false;
            }
        },

        async handleUpdateNote() {
            try {
                const clientId = this.$route.params.id;

                if (!clientId || !this.noteValue || !this.currentNoteId) {
                    this.$toast.error("Missing note id"); 
                    return;               
                }

                if (!this.noteValue) {
                    this.$toast.error("Note value is required"); 
                    return;                     
                }

                this.loading = true;

                const payload = {
                    _id: this.currentNoteId,
                    refId: clientId,
                    value: replaceStringsAll(this.noteValue, /<\/?p>/g),
                };

                const res = await this.$store.dispatch("clients/updateNote", payload);
                
                if (!res) {
                    this.$toast.error("Unable to update note");
                    return;
                }

                this.notes = this.notes.map(c => {
                    if (c?._id === res?._id) {
                        return {
                            ...c,
                            ...res,
                        };
                    } else {
                        return c;
                    }
                });

                this.$toast.success("Note updated");
                this.resetForm();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.loading = false;
            }            
        },

        handleFormSubmit() {
            if (this.action === "add") {
                this.handleAddNote();
            } else {
                this.handleUpdateNote();
            }
        },

        async handleDeleteNote(item) {
            try {
                if (!item._id) {
                    this.$toast.error("Missing note id required"); 
                    return;               
                }

                this.currentNoteId = item._id;
                this.deleteLoading = true;

                await this.$store.dispatch("clients/deleteNote", {
                    clientId: this.$route.params.id,
                    _id: item._id,
                });

                this.notes = this.notes.filter(c => c._id !== item._id);
                this.pageData.total = this.pageData.total - 1;
                this.pageData.totalPages = Math.ceil(this.pageData.total / 50);

                this.$toast.success("Note deleted");
                this.resetForm();
            } catch (error) {
                const errMsg = error?.response?.data?.message || error?.message;
                this.$toast.error(errMsg);
            } finally {
                this.deleteLoading = false;
                this.currentNoteId = "";
            }      
        },

        handleCopyNoteContent(note) {
            if (!note._id && note.value) return;

            navigator.clipboard.writeText(note.value);
        },
    },
};
</script>

<style scoped lang="scss">
    textarea.form-control {
        border-radius: 4px !important;
    }
</style>